import { gql } from '@apollo/client';
import { EAP_PROFILE_FIELDS } from './eap-profile';

/**
 * Network profile (renamed FE-side from `wifi-info`) — CRUD operations.
 *
 * Spec: docs/specs/19-optiedge-mdm/802.1x/frank/01-plan-auth-profile.md §A.1.
 *
 * Note: the backend GraphQL ObjectType + Mongo collection retain the name
 * `WifiInfo` / `wifiInfos`. The frontend renames TS-side identifiers to
 * `NetworkProfile` for UI clarity. GraphQL operation field names match the
 * backend (`wifiInfos`, `saveWifiInfo`, `deleteWifiInfo`) — Apollo
 * `refetchQueries` strings use those names. Push/remove operations renamed
 * in Step 6 (`pushNetworkProfile`, `removeNetworkProfile`) — see
 * `./network-profile-push.ts`.
 *
 * Step 3 dropped the enterprise auth fields from `WifiInfoInput`; this
 * fragment also no longer selects them. Step 5 added `name` + `eapProfileId`.
 */

export const NETWORK_PROFILE_FIELDS = gql`
  fragment NetworkProfileFields on WifiInfo {
    _id
    name
    ssid
    connection
    security
    psk
    hidden
    eapProfileId
    encrypted
    createdAt
  }
`;

export const NETWORK_PROFILES_QUERY = gql`
  query wifiInfos($teamId: String, $filter: QueryWifiInfoInput) {
    wifiInfos(teamId: $teamId, filter: $filter) {
      ...NetworkProfileFields
    }
  }
  ${NETWORK_PROFILE_FIELDS}
`;

/**
 * Combined query — network profiles + all EapProfile rows for the team.
 * Frontend uses this in the Network Profiles tab to display the EAP profile
 * name in the row (resolved client-side from the two arrays).
 */
export const NETWORK_PROFILES_WITH_EAP_QUERY = gql`
  query networkProfilesWithEap($teamId: String, $filter: QueryWifiInfoInput) {
    wifiInfos(teamId: $teamId, filter: $filter) {
      ...NetworkProfileFields
    }
    eapProfiles(teamId: $teamId) {
      ...EapProfileFields
    }
  }
  ${NETWORK_PROFILE_FIELDS}
  ${EAP_PROFILE_FIELDS}
`;

export const SAVE_NETWORK_PROFILE = gql`
  mutation saveWifiInfo($teamId: String, $payload: WifiInfoInput!) {
    saveWifiInfo(teamId: $teamId, payload: $payload) {
      ...NetworkProfileFields
    }
  }
  ${NETWORK_PROFILE_FIELDS}
`;

export const DELETE_NETWORK_PROFILE = gql`
  mutation deleteWifiInfo($_id: String!) {
    deleteWifiInfo(_id: $_id)
  }
`;
